import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Sparkles, X } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';

export interface FillerFieldDef {
  name: string;
  label: string;
  type: string;
  required?: boolean;
}

interface FillResponse {
  values: Record<string, string | null>;
  confidence: Record<string, number>;
  unparsed: string;
  provider: string;
  model: string;
}

const HIGH_CONFIDENCE = 0.7;
const MID_CONFIDENCE = 0.4;

/**
 * Story 49.6 — natural-language form filler.
 *
 * Renders a sparkles button. Click → modal with textarea. Submit → calls
 * /api/v1/ai/form-fill with the field schema + raw text. Values with
 * confidence ≥ 0.7 are auto-applied via onApply; values in 0.4–0.7 are
 * surfaced as one-click suggestions; below 0.4 are dropped.
 *
 * Returns null (renders nothing) when the tenant has no LLM provider
 * configured — graceful degradation per the AC.
 */
export function NaturalLanguageFiller({
  fieldSchema,
  // currentValues kept in the prop API for future "diff against current"
  // UX; not consumed by the current minimum-viable extraction flow.
  currentValues: _currentValues,
  onApply,
  buttonClassName,
}: {
  fieldSchema: FillerFieldDef[];
  currentValues?: Record<string, unknown>;
  onApply: (values: Record<string, string>) => void;
  buttonClassName?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [providerAvailable, setProviderAvailable] = useState<boolean | null>(null);
  const [providerName, setProviderName] = useState<string | null>(null);
  const [providerModel, setProviderModel] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<FillResponse | null>(null);

  // Probe AI availability so we can hide the icon entirely when no
  // provider is configured (AC: graceful degradation).
  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ available: boolean; provider: string | null; model: string | null }>('/ai/status')
      .then(({ data }) => {
        if (cancelled) return;
        setProviderAvailable(data.available);
        setProviderName(data.provider);
        setProviderModel(data.model);
      })
      .catch(() => {
        if (cancelled) return;
        // Probe failed → fall back to "available" so the user can at
        // least try; the real /form-fill call will report errors.
        setProviderAvailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (providerAvailable === false) return null;

  async function processText() {
    if (!text.trim()) return;
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await apiClient.post<FillResponse>('/ai/form-fill', {
        fieldSchema,
        rawText: text,
      });
      setResult(data);

      // Auto-apply high-confidence values immediately.
      const auto: Record<string, string> = {};
      for (const field of fieldSchema) {
        const v = data.values[field.name];
        const c = data.confidence[field.name] ?? 0;
        if (v != null && v !== '' && c >= HIGH_CONFIDENCE) {
          auto[field.name] = v;
        }
      }
      if (Object.keys(auto).length > 0) onApply(auto);
    } catch (err) {
      if (isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 503 || status === 502) {
          setError(t('ai.filler_unavailable'));
        } else {
          const detail = (err.response?.data as { detail?: string } | undefined)?.detail;
          setError(detail ?? t('ai.filler_unavailable'));
        }
      } else {
        setError(t('ai.filler_unavailable'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function applySuggestion(name: string, value: string) {
    onApply({ [name]: value });
    // Keep the modal open so the user can keep applying suggestions.
  }

  function close() {
    setOpen(false);
    setText('');
    setResult(null);
    setError(null);
  }

  // Mid-confidence suggestions to surface as one-click chips.
  const suggestions: Array<{ name: string; label: string; value: string; confidence: number }> =
    result
      ? fieldSchema
          .map((f) => {
            const v = result.values[f.name];
            const c = result.confidence[f.name] ?? 0;
            if (v == null || v === '' || c >= HIGH_CONFIDENCE || c < MID_CONFIDENCE) return null;
            return { name: f.name, label: f.label, value: v, confidence: c };
          })
          .filter((x): x is { name: string; label: string; value: string; confidence: number } => x !== null)
      : [];

  const noMatch =
    result &&
    fieldSchema.every((f) => {
      const v = result.values[f.name];
      return v == null || v === '';
    });

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={t('ai.filler_tooltip')}
        aria-label={t('ai.filler_tooltip')}
        className={
          buttonClassName ??
          'inline-flex items-center justify-center rounded-md p-1.5 text-primary hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary/40'
        }
      >
        <Sparkles className="h-4 w-4" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-card p-6 shadow-lg">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">{t('ai.filler_title')}</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t('ai.filler_description')}
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                className="rounded p-1 text-muted-foreground hover:text-foreground"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <textarea
              autoFocus
              rows={8}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('ai.filler_placeholder')}
              className="w-full rounded-md border bg-background p-3 text-sm"
            />

            {providerName && (
              <p className="mt-2 text-[11px] text-muted-foreground">
                {t('ai.filler_provider_note', {
                  provider: providerName,
                  model: providerModel ?? '—',
                })}
              </p>
            )}

            {error && (
              <p className="mt-2 text-sm text-destructive">{error}</p>
            )}

            {result && noMatch && (
              <p className="mt-3 rounded-md bg-muted p-3 text-sm text-muted-foreground">
                {t('ai.filler_no_match')}
              </p>
            )}

            {suggestions.length > 0 && (
              <div className="mt-3 space-y-2">
                {suggestions.map((s) => (
                  <div
                    key={s.name}
                    className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 p-2 text-sm"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-muted-foreground">
                        {s.label} · {Math.round(s.confidence * 100)}%
                      </div>
                      <div className="truncate">{s.value}</div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => applySuggestion(s.name, s.value)}
                    >
                      {t('ai.filler_suggestion_use')}
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={close} disabled={submitting}>
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={processText}
                disabled={submitting || !text.trim()}
              >
                {submitting ? t('ai.filler_processing') : t('ai.filler_process')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
